import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Ban, Clock, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetExpenseQuery } from "@/redux/services/expense/expense.api";
import {
  getExpenseCategoryLabel,
  getExpenseVoidMessage,
  getPaymentMethodLabel,
  getVoidDrawerNote,
  isClosedSessionVoidBlocked,
} from "@/lib/expenses";
import { displayPrice, formatDate, isGranted } from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import VoidExpenseModal from "./VoidExpenseModal";

interface ExpenseDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  expenseId: string | null;
}

const Field = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm font-medium text-card-foreground break-words">
      {value}
    </p>
  </div>
);

const TimelineEntry = ({
  title,
  who,
  at,
}: {
  title: string;
  who?: string | null;
  at?: string | null;
}) => (
  <div className="flex items-start gap-3 text-sm">
    <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-muted-foreground" />
    <div className="min-w-0 flex-1">
      <p className="font-medium text-card-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">
        {who ?? "Système"}
        {at ? ` · ${formatDate(new Date(at))}` : ""}
      </p>
    </div>
  </div>
);

const ExpenseDetailsDrawer = ({
  isOpen,
  onClose,
  expenseId,
}: ExpenseDetailsDrawerProps) => {
  const currency = useRestaurantCurrency();
  const { can } = usePermissions();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  // RESTAURANT_MANAGER on the API: a manager of the restaurant or its owner.
  const isRestaurantManager = isGranted(
    [Roles.manager, Roles.owner],
    currentUser?.roles
  );
  const [isVoidOpen, setIsVoidOpen] = useState(false);

  const { data: expense, isLoading } = useGetExpenseQuery(
    { expenseUuid: expenseId ?? "" },
    { skip: !expenseId },
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isVoided = expense?.status === "voided";
  const voidDrawerNote = expense ? getVoidDrawerNote(expense) : undefined;
  const voidBlocked = expense
    ? isClosedSessionVoidBlocked(expense, isRestaurantManager)
    : false;

  return createPortal(
    <>
      {/* Backdrop */}
      <button
        className="fixed inset-0 z-40 cursor-default bg-black/40"
        onClick={onClose}
        aria-label="Fermer le panneau"
      />

      {/* Side panel */}
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-card-foreground">
              {expense ? getExpenseCategoryLabel(expense.category) : "Dépense"}
            </h2>
            {expense && (
              <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {formatDate(new Date(expense.createdAt))}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {expense && (
            <div className="space-y-4">
              {/* Status badges */}
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={
                    isVoided
                      ? "inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground"
                      : "inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700"
                  }
                >
                  {isVoided ? "Annulée" : "Enregistrée"}
                </span>
                {expense.paidFromDrawer && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-muted-foreground ring-1 ring-inset ring-border">
                    Payé depuis la caisse
                  </span>
                )}
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Montant"
                  value={displayPrice(expense.amount, currency)}
                />
                <Field
                  label="Paiement"
                  value={getPaymentMethodLabel(expense.paymentMethod)}
                />
                <Field
                  label="Date de la dépense"
                  value={formatDate(new Date(expense.incurredAt), false)}
                />
                <Field label="Bénéficiaire" value={expense.payee ?? "—"} />
                {expense.purchaseOrder && (
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Bon de commande
                    </p>
                    {can("suppliers.view") ? (
                      <Link
                        to={`/inventory/purchase-orders/${expense.purchaseOrder.id}`}
                        onClick={onClose}
                        className="text-sm font-medium text-primary hover:underline break-words"
                      >
                        {expense.purchaseOrder.poNumber}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium text-card-foreground break-words">
                        {expense.purchaseOrder.poNumber}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              {expense.description && (
                <>
                  <div className="border-t border-dashed border-border" />
                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">
                      Description
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-card-foreground">
                      {expense.description}
                    </p>
                  </div>
                </>
              )}

              {/* Void reason callout */}
              {isVoided && (
                <div className="space-y-1.5 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <div className="flex items-center gap-2 text-destructive">
                    <Ban className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm font-medium">Dépense annulée</p>
                  </div>
                  {expense.voidReason && (
                    <p className="text-sm text-card-foreground">
                      {expense.voidReason}
                    </p>
                  )}
                  {voidDrawerNote && (
                    <p className="text-xs text-muted-foreground">
                      {voidDrawerNote}
                    </p>
                  )}
                </div>
              )}

              {/* Dashed separator */}
              <div className="border-t border-dashed border-border" />

              {/* Historique timeline */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-card-foreground">
                  Historique
                </h3>
                <div className="space-y-2">
                  {isVoided && (
                    <TimelineEntry
                      title="Annulée"
                      who={expense.voidedBy?.fullName}
                      at={expense.voidedAt}
                    />
                  )}
                  <TimelineEntry
                    title="Enregistrée"
                    who={expense.createdBy?.fullName}
                    at={expense.createdAt}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer: void action */}
        {expense && !isVoided && can("expenses.void") && voidBlocked && (
          <div className="border-t border-border px-5 py-4">
            <p className="text-xs text-muted-foreground">
              {getExpenseVoidMessage("expense.void.closed_session_manager_only")}
            </p>
          </div>
        )}
        {expense && !isVoided && can("expenses.void") && !voidBlocked && (
          <div className="border-t border-border px-5 py-4">
            <Button
              variant="outline"
              className="w-full border-destructive/40 text-destructive hover:bg-destructive/5"
              onClick={() => setIsVoidOpen(true)}
            >
              <Ban className="mr-2 h-4 w-4" />
              Annuler la dépense
            </Button>
          </div>
        )}
      </div>

      {expense && isVoidOpen && (
        <VoidExpenseModal
          isOpen={isVoidOpen}
          onClose={() => setIsVoidOpen(false)}
          expense={expense}
        />
      )}
    </>,
    document.body,
  );
};

export default ExpenseDetailsDrawer;
